import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { CheckCircle, MessageCircle, Mail } from 'lucide-react';

import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';

import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { useSiteSettings } from '../hooks/useSiteSettings';
import { calculateDiscount, formatPrice } from '../lib/utils';

const BASE_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const API_URL = `${BASE_URL}/api`;


const toAbsoluteUrl = (url) => {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  const cleaned = url.startsWith('/') ? url : `/${url}`;
  return BASE_URL ? `${BASE_URL}${cleaned}` : cleaned;
};

const FALLBACK_IMG =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="128" height="128" viewBox="0 0 128 128">
      <rect width="128" height="128" fill="#f1f5f9"/>
      <path d="M34 88l16-18 14 16 10-12 20 24H34z" fill="#cbd5e1"/>
      <circle cx="50" cy="46" r="8" fill="#cbd5e1"/>
    </svg>`
  );

const isUUID = (s) =>
  typeof s === 'string' &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(s);

const getItemImage = (item) => {
  const direct = item?.image || item?.image_url || item?.thumbnail || item?.thumb;
  if (direct) return toAbsoluteUrl(direct);

  const imgs = item?.images;
  if (Array.isArray(imgs) && imgs.length) return toAbsoluteUrl(imgs[0]);

  if (typeof imgs === 'string' && imgs.trim()) {
    const first = imgs.split(',')[0]?.trim();
    if (first) return toAbsoluteUrl(first);
  }

  return FALLBACK_IMG;
};

// Kosovo municipalities (for easy search + consistent shipping rates)
const KOSOVO_CITIES = [
  'Prishtinë',
  'Podujevë',
  'Fushë Kosovë',
  'Obiliq',
  'Lipjan',
  'Graçanicë',
  'Drenas',
  'Shtime',
  'Ferizaj',
  'Kaçanik',
  'Hani i Elezit',
  'Shtërpcë',
  'Viti',
  'Gjilan',
  'Kamenicë',
  'Novobërdë',
  'Ranillugu',
  'Partesh',
  'Kllokot',
  'Vushtrri',
  'Mitrovicë',
  'Mitrovicë e Veriut',
  'Skenderaj',
  'Sërbicë',
  'Leposaviq',
  'Zubin Potok',
  'Zveçan',
  'Pejë',
  'Istog',
  'Klinë',
  'Deçan',
  'Junik',
  'Gjakovë',
  'Rahovec',
  'Malishevë',
  'Prizren',
  'Suharekë',
  'Dragash',
  'Mamushë',
];

const normalizeKey = (value) =>
  (value || '')
    .toString()
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

const parseCityRates = (raw) => {
  if (!raw) return {};
  try {
    const obj = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (!obj || typeof obj !== 'object') return {};
    const map = {};
    for (const [k, v] of Object.entries(obj)) {
      const n = Number(v);
      if (Number.isFinite(n)) map[normalizeKey(k)] = n;
    }
    return map;
  } catch {
    return {};
  }
};

const safeItemPrice = (item) => {
  const raw = Number(item?.price ?? item?.unit_price ?? 0);
  return Number.isFinite(raw) ? raw : 0;
};

const effectiveItemPrice = (item) => {
  const base = safeItemPrice(item);
  const d = Number(item?.discount ?? 0);
  if (Number.isFinite(d) && d > 0) return calculateDiscount(base, d);
  return base;
};

const buildOrderMessage = ({ items, total, shippingFee, customer, orderId }) => {
  const lines = [];
  lines.push('🛍️ Order Request');
  if (orderId) lines.push(`Order ID: ${orderId}`);
  lines.push('');
  lines.push('Items:');
  items.forEach((it, idx) => {
    const lineTotal = effectiveItemPrice(it) * (Number(it.quantity) || 1);
    lines.push(`${idx + 1}) ${it.name} x${it.quantity} — ${formatPrice(lineTotal)}`);
  });
  lines.push('');
  lines.push(`Subtotal: ${formatPrice(total - shippingFee)}`);
  lines.push(`Shipping: ${formatPrice(shippingFee)}`);
  lines.push(`Total: ${formatPrice(total)}`);
  lines.push('');
  lines.push('Delivery details:');
  lines.push(`Name: ${customer.name}`);
  lines.push(`Phone: ${customer.phone}`);
  if (customer.email) lines.push(`Email: ${customer.email}`);
  lines.push(`Address: ${customer.address}`);
  lines.push(`City: ${customer.city}`);
  if (customer.postal_code) lines.push(`Postal code: ${customer.postal_code}`);
  if (customer.notes) {
    lines.push('');
    lines.push(`Notes: ${customer.notes}`);
  }
  return lines.join('\n');
};

export const Checkout = () => {
  const { t } = useTranslation();
  const { cart, clearCart, getCartTotal } = useCart();
  const { token } = useAuth();
  const { settings } = useSiteSettings();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postal_code: '',
    notes: '',
  });

  const [loading, setLoading] = useState(false);
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [placedOrderData, setPlacedOrderData] = useState(null);

  const cartSubtotal = useMemo(() => getCartTotal(), [cart, getCartTotal]);

  const baseShippingFee = useMemo(() => {
    const n = Number(settings?.shipping_fee ?? 0);
    return Number.isFinite(n) ? n : 0;
  }, [settings]);

  const freeShippingThreshold = useMemo(() => {
    const n = Number(settings?.free_shipping_threshold ?? 0);
    return Number.isFinite(n) ? n : 0;
  }, [settings]);

  const cityRates = useMemo(() => parseCityRates(settings?.shipping_city_rates), [settings]);

  const qualifiesFreeShipping = useMemo(() => {
    return freeShippingThreshold > 0 && cartSubtotal >= freeShippingThreshold;
  }, [freeShippingThreshold, cartSubtotal]);

  const { shippingFee, matchedCityKey } = useMemo(() => {
    if (qualifiesFreeShipping) return { shippingFee: 0, matchedCityKey: null };

    const key = normalizeKey(formData.city);
    const rate = key && Object.prototype.hasOwnProperty.call(cityRates, key) ? cityRates[key] : null;
    const fee = rate != null ? rate : baseShippingFee;
    return { shippingFee: fee, matchedCityKey: key && rate != null ? key : null };
  }, [qualifiesFreeShipping, formData.city, cityRates, baseShippingFee]);

  const orderTotal = useMemo(() => cartSubtotal + shippingFee, [cartSubtotal, shippingFee]);

  const validateForm = () => {
    if (!formData.name.trim()) return t('checkout.nameRequired', 'Name is required');
    if (!formData.phone.trim()) return t('checkout.phoneRequired', 'Phone is required');
    if (!formData.address.trim()) return t('checkout.addressRequired', 'Address is required');
    if (!formData.city.trim()) return t('checkout.cityRequired', 'City is required');
    return null;
  };

  const sendViaWhatsApp = (message, targetWindow) => {
    const raw = (settings?.whatsapp_number || '').toString().trim();
    if (!raw) {
      toast.error(t('checkout.whatsappMissing', 'WhatsApp number is not configured in Admin Settings.'));
      return;
    }
    const digits = raw.replace(/\D/g, '');
    if (!digits) {
      toast.error(t('checkout.whatsappInvalid', 'WhatsApp number is invalid.'));
      return;
    }
    const url = `https://wa.me/${digits}?text=${encodeURIComponent(message)}`;

    try {
      if (targetWindow && !targetWindow.closed) {
        targetWindow.location.href = url;
        return;
      }
    } catch {
      // fall through
    }

    // Fallback if popup was blocked or unavailable
    const w = window.open(url, '_blank');
    if (!w) window.location.href = url;
  };

  const sendViaEmail = (message, targetWindow) => {
    const to = (settings?.order_email || '').toString().trim();
    if (!to) {
      toast.error(t('checkout.emailMissing', 'Order email is not configured in Admin Settings.'));
      return;
    }
    const subject = encodeURIComponent('Order Request');
    const body = encodeURIComponent(message);
    const link = `mailto:${to}?subject=${subject}&body=${body}`;

    try {
      if (targetWindow && !targetWindow.closed) {
        targetWindow.location.href = link;
        return;
      }
    } catch {
      // fall through
    }

    window.location.href = link;
  };

  const placeOrder = async (method) => {
    const errorMsg = validateForm();
    if (errorMsg) {
      toast.error(errorMsg);
      return;
    }

    if (!cart || cart.length === 0) {
      toast.error(t('checkout.cartEmpty', 'Your cart is empty.'));
      return;
    }

    // Open a blank tab synchronously to avoid popup blockers (we'll redirect it after the order is created)
    let channelWindow = null;
    if (method === 'whatsapp' || method === 'email') {
      try {
        channelWindow = window.open('about:blank', '_blank');
      } catch {
        channelWindow = null;
      }
    }

    setLoading(true);
    try {
      // Ensure items are safe (some backends require UUID product_id)
      // Build items both for UI (message/summary) and for API (backend schema)
      const orderItems = cart.map((item) => ({
        id: item.id,
        name: (item.name || '').toString(),
        quantity: Number(item.quantity) || 1,
        unit_price: safeItemPrice(item),
        discount: Number(item.discount) || 0,
        image: getItemImage(item),
      }));

      const itemsForApi = cart.map((item) => {
        const qty = Number(item.quantity) || 1;
        const price = Number(effectiveItemPrice(item)) || 0;
        const payload = {
          product_name: (item.name || '').toString(),
          quantity: qty,
          price,
        };
        if (isUUID(item.id)) payload.product_id = item.id;
        return payload;
      });

      const notesParts = [];
      if ((formData.notes || '').toString().trim()) notesParts.push((formData.notes || '').toString().trim());
      notesParts.push(`Customer: ${formData.name}${formData.email ? ' <' + formData.email + '>' : ''}`);
      notesParts.push(`Shipping: ${formatPrice(shippingFee)}`);
      notesParts.push(`Total (incl shipping): ${formatPrice(orderTotal)}`);
      const mergedNotes = notesParts.filter(Boolean).join('\n');

      const orderData = {
        phone: (formData.phone || '').toString().trim() || null,
        city: (formData.city || '').toString().trim() || null,
        postal_code: (formData.postal_code || '').toString().trim() || null,
        delivery_address: (formData.address || '').toString().trim() || null,
        notes: mergedNotes || null,
        payment_method: null,
        items: itemsForApi,
      };


      const authToken = token || localStorage.getItem('token');
      const headers = authToken ? { Authorization: `Bearer ${authToken}` } : undefined;

      const response = await axios.post(`${API_URL}/orders`, orderData, { headers });
      const orderId =
        response?.data?.id ||
        response?.data?.order_id ||
        response?.data?.order?.id ||
        response?.data?.order?.order_id ||
        response?.data?.uuid ||
        '';

      const placed = {
        ...formData,
        orderId,
        items: orderItems,
        shippingFee,
        total: orderTotal,
      };

      setPlacedOrderData(placed);
      setOrderPlaced(true);

      const message = buildOrderMessage({
        items: orderItems,
        total: orderTotal,
        shippingFee,
        customer: {
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          address: formData.address,
          city: formData.city,
          postal_code: formData.postal_code,
          notes: formData.notes,
        },
        orderId,
      });

      // Open the chosen channel
      if (method === 'whatsapp') sendViaWhatsApp(message, channelWindow);
      if (method === 'email') sendViaEmail(message, channelWindow);

      clearCart();
      toast.success(t('checkout.orderSuccess', 'Order created successfully!'));
    } catch (error) {
      // Close the pre-opened tab if the order failed
      try {
        if (channelWindow && !channelWindow.closed) channelWindow.close();
      } catch {
        // ignore
      }
      console.error('Checkout error:', error);
      const msg = error?.response?.data?.detail || error?.response?.data?.message || t('checkout.orderFailed', 'Failed to place order');
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (!cart || cart.length === 0) {
    return (
      <div className="min-h-screen bg-background pt-24">
        <div className="container mx-auto px-4 py-8 max-w-4xl">
          <Card className="p-8 text-center">
            <h2 className="text-2xl font-bold mb-4">{t('checkout.emptyCart', 'Your cart is empty')}</h2>
            <p className="text-muted-foreground">{t('checkout.emptyCartDesc', 'Add some products before checking out.')}</p>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-24">
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <h1 className="text-3xl font-bold">{t('checkout.title', 'Checkout')}</h1>
          <p className="text-muted-foreground mt-2">{t('checkout.subtitle', 'Complete your order details')}</p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Delivery Form */}
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
            <Card className="p-6">
              <h2 className="text-xl font-semibold mb-6">{t('checkout.deliveryInfo', 'Delivery Information')}</h2>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="name">{t('checkout.name', 'Full Name')} *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder={t('checkout.namePlaceholder', 'Enter your full name')}
                  />
                </div>

                <div>
                  <Label htmlFor="email">{t('checkout.email', 'Email')}</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder={t('checkout.emailPlaceholder', 'Enter your email')}
                  />
                </div>

                <div>
                  <Label htmlFor="phone">{t('checkout.phone', 'Phone')} *</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder={t('checkout.phonePlaceholder', 'Enter your phone number')}
                  />
                </div>

                <div>
                  <Label htmlFor="address">{t('checkout.address', 'Address')} *</Label>
                  <Textarea
                    id="address"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder={t('checkout.addressPlaceholder', 'Enter your full address')}
                    rows={3}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="city">{t('checkout.city', 'City')} *</Label>
                    <Input
                      id="city"
                      list="kosovo-cities"
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      placeholder={t('checkout.cityPlaceholder', 'Select or search city')}
                    />
                    <datalist id="kosovo-cities">
                      {KOSOVO_CITIES.map((c) => (
                        <option key={c} value={c} />
                      ))}
                    </datalist>
                  </div>

                  <div>
                    <Label htmlFor="postal_code">{t('checkout.postalCode', 'Postal Code')}</Label>
                    <Input
                      id="postal_code"
                      value={formData.postal_code}
                      onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                      placeholder={t('checkout.postalPlaceholder', 'e.g. 10000')}
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="notes">{t('checkout.notes', 'Order Notes')}</Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder={t('checkout.notesPlaceholder', 'Optional notes for delivery')}
                    rows={2}
                  />
                </div>

                <div className="pt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Button
                    type="button"
                    onClick={() => placeOrder('whatsapp')}
                    className="w-full"
                    disabled={loading}
                  >
                    <MessageCircle className="w-4 h-4 mr-2" />
                    {loading ? t('checkout.processing', 'Processing...') : t('checkout.whatsapp', 'Checkout via WhatsApp')}
                  </Button>

                  <Button
                    type="button"
                    onClick={() => placeOrder('email')}
                    className="w-full"
                    variant="outline"
                    disabled={loading}
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    {loading ? t('checkout.processing', 'Processing...') : t('checkout.emailOption', 'Checkout via Email')}
                  </Button>
                </div>

                <p className="text-xs text-muted-foreground">
                  {t('checkout.adminConfigHint', 'WhatsApp number and order email are configured from the Admin panel.')}
                </p>
              </div>
            </Card>
          </motion.div>

          {/* Order Summary */}
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
            <Card className="p-6 sticky top-28">
              <h2 className="text-xl font-semibold mb-6">{t('checkout.orderSummary', 'Order Summary')}</h2>

              <div className="space-y-4 mb-6 max-h-64 overflow-y-auto">
                {cart.map((item) => {
                  const unit = effectiveItemPrice(item);
                  return (
                    <div key={item.id} className="flex items-center gap-4 pb-4 border-b last:border-b-0 last:pb-0">
                      <img
                        src={getItemImage(item)}
                        alt={item.name}
                        className="w-16 h-16 object-cover rounded"
                        onError={(e) => {
                          e.currentTarget.src = FALLBACK_IMG;
                        }}
                      />
                      <div className="flex-1">
                        <h3 className="font-medium">{item.name}</h3>
                        <p className="text-sm text-muted-foreground">
                          {t('checkout.quantity', 'Qty')}: {item.quantity}
                        </p>
                        {Number(item.discount) > 0 && (
                          <Badge variant="secondary" className="mt-1">
                            -{item.discount}%
                          </Badge>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="font-medium">{formatPrice(unit * (Number(item.quantity) || 1))}</p>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="space-y-3 pt-4 border-t">
                <div className="flex justify-between">
                  <span>{t('checkout.subtotal', 'Subtotal')}</span>
                  <span>{formatPrice(cartSubtotal)}</span>
                </div>

                <div className="flex justify-between">
                  <span>{t('checkout.shipping', 'Shipping')}</span>
                  <span>{shippingFee === 0 ? t('checkout.free', 'Free') : formatPrice(shippingFee)}</span>
                </div>

                {qualifiesFreeShipping && (
                  <p className="text-sm text-success">
                    {t('checkout.freeShippingApplied', 'Free shipping applied (threshold reached)!')}
                  </p>
                )}

                {matchedCityKey && shippingFee > 0 && (
                  <p className="text-sm text-muted-foreground">
                    {t('checkout.cityRateApplied', 'City shipping rate applied.')}
                  </p>
                )}

                <div className="flex justify-between font-semibold text-lg pt-3 border-t">
                  <span>{t('checkout.total', 'Total')}</span>
                  <span>{formatPrice(orderTotal)}</span>
                </div>

                {freeShippingThreshold > 0 && !qualifiesFreeShipping && (
                  <p className="text-xs text-muted-foreground">
                    {t('checkout.freeShippingHint', 'Free shipping for orders above')} {formatPrice(freeShippingThreshold)}.
                  </p>
                )}
              </div>
            </Card>
          </motion.div>
        </div>

        {orderPlaced && placedOrderData && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-8">
            <Card className="p-6">
              <div className="flex items-center gap-3 mb-2">
                <CheckCircle className="w-6 h-6 text-success" />
                <h3 className="text-lg font-semibold">{t('checkout.done', 'Order created')}</h3>
              </div>
              <p className="text-muted-foreground">
                {placedOrderData.orderId ? (
                  <>
                    {t('checkout.orderId', 'Order ID')}: <span className="font-medium">{placedOrderData.orderId}</span>
                  </>
                ) : (
                  t('checkout.noOrderId', 'Order ID not returned by server.')
                )}
              </p>
            </Card>
          </motion.div>
        )}
      </div>
    </div>
  );
};
